// 施工実施(Construction)を「登録日の降順」で表として表示。
// 工事フィルタは選択された work_id 配下の area を経由した Construction だけに絞る。

import { useMemo } from "react";
import { Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useNavigate } from "react-router-dom";
import { ExtendedDB } from "../../../types/extendedDB";
import { DataLink } from "../../../components/links/EntityLinks";
import { ConstructionCompletionState, constructionCompletionState, constructionStatusLabel } from "../../../util/constructionStatus";
import {
  formatDate,
  workName,
  workSubtypeName,
} from "./helpers";

type Row = {
  id: string;
  executed_at: number | null;
  createdAt: number;
  finished_at: number | null;
  works: string;
  subtypes: string;
  areaCount: number;
  peCount: number;
  completedTargets: number;
  temp: number | null;
  humid: number | null;
  consumedQrCount: number;
  completionState: ConstructionCompletionState;
};

export default function ConstructionRecordTable({ db, workFilter }: { db: ExtendedDB; workFilter: number | "" }) {
  const navigate = useNavigate();

  const rows = useMemo<Row[]>(() => {
    const src = db.constructions.filter((c) => c.deleted_at == null);
    const sorted = src.slice().sort((a, b) => b.createdAt - a.createdAt);
    const out: Row[] = [];
    for (const c of sorted) {
      const pes = db.processExecutions.filter((p) => p.construction_id === c.id && p.deleted_at == null);
      const areaIds = new Set(pes.map((p) => p.construction_plan_area_id));
      const areas = db.constructionPlanAreas.filter((a) => areaIds.has(a.id) && a.deleted_at == null);
      const workIds = new Set(areas.map((a) => a.work_id).filter((id): id is number => id != null));
      if (workFilter !== "" && !workIds.has(workFilter)) {
        continue;
      }
      const workNames = Array.from(workIds).map((wid) => workName(db, wid));
      const subtypeIds = Array.from(new Set(areas.map((a) => a.work_subtype_id).filter((id): id is number => id != null)));
      const subtypeNames = subtypeIds.map((sid) => workSubtypeName(db, sid));
      const consumed = db.constructionMaterialConsumptions.filter((m) => m.construction_id === c.id && m.deleted_at == null);
      out.push({
        id: c.id,
        executed_at: c.executed_at,
        createdAt: c.createdAt,
        finished_at: c.finished_at,
        works: workNames.join(" / "),
        subtypes: subtypeNames.join(" / "),
        areaCount: areas.length,
        peCount: pes.length,
        completedTargets: pes.filter((p) => p.completed_at != null).length,
        temp: c.ambient_temperature,
        humid: c.ambient_humidity,
        consumedQrCount: consumed.length,
        completionState: constructionCompletionState({ finished_at: c.finished_at, processExecutions: pes }),
      });
    }
    return out;
  }, [db, workFilter]);

  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        まだ施工実施は記録されていません。「新規施工実施」から登録してください。
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", width: 90 }}>状態</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 140 }}>ID</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 120 }}>施工日</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 120 }}>登録日</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>工事</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>種別</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">施工箇所数</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">工程実施数</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">完了マーク数</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">温度</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">湿度</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">消費QR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const isCompleted = r.completionState === "completed";
            return (
              <TableRow
                key={r.id}
                hover
                sx={!isCompleted ? { backgroundColor: "rgba(255, 152, 0, 0.05)", cursor: "pointer" } : { cursor: "pointer" }}
                onClick={() => navigate(`${r.id}/`)}
              >
                <TableCell>
                  <Chip
                    size="small"
                    color={isCompleted ? "success" : "warning"}
                    icon={isCompleted ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={constructionStatusLabel(r.completionState)}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DataLink type="construction" id={r.id} />
                </TableCell>
                <TableCell>{formatDate(r.executed_at)}</TableCell>
                <TableCell>{formatDate(r.createdAt)}</TableCell>
                <TableCell><Typography variant="body2">{r.works || "-"}</Typography></TableCell>
                <TableCell><Typography variant="body2">{r.subtypes || "-"}</Typography></TableCell>
                <TableCell align="right">{r.areaCount}</TableCell>
                <TableCell align="right">{r.peCount}</TableCell>
                <TableCell align="right">{r.completedTargets}</TableCell>
                <TableCell align="right">{r.temp != null ? `${r.temp.toFixed(1)}℃` : "-"}</TableCell>
                <TableCell align="right">{r.humid != null ? `${r.humid.toFixed(0)}%` : "-"}</TableCell>
                <TableCell align="right">{r.consumedQrCount}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}
